import { motion } from "framer-motion";
import { MapPin, Phone, CheckCircle, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import QuickContactForm from "@/components/QuickContactForm";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for default marker icon
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const cities = [
  "Houston", "Pearland", "League City", "Sugar Land", "Missouri City",
  "Stafford", "Bellaire", "West University Place", "Humble", "Kingwood",
  "Cypress", "Tomball", "The Woodlands", "Spring", "Katy",
  "Richmond", "Rosenberg", "Friendswood", "Clear Lake", "Pasadena",
  "Deer Park", "La Porte", "Galena Park", "Jacinto City", "South Houston"
];

export default function ServiceArea() {
  return (
    <div className="min-h-screen bg-background">
      {/* Map Section - Full Width at Top */}
      <section className="w-full h-[500px]">
        <MapContainer
          center={[29.8604, -95.3598]}
          zoom={13}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
          className="w-full h-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[29.8604, -95.3598]}>
            <Popup>
              <div className="text-center font-medium">
                <p className="font-bold text-foreground">Eastex Freeway @ Little York Rd</p>
                <p className="text-sm text-muted-foreground">Houston, Texas</p>
              </div>
            </Popup>
          </Marker>
        </MapContainer>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Our Service Area
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We proudly serve Houston and surrounding areas with on-time delivery and professional setup
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            {/* Coverage Area */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card rounded-2xl shadow-xl p-8 border border-border/50 lg:col-span-3"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Coverage Area</h2>
              </div>
              
              <p className="text-foreground/80 mb-6 leading-relaxed">
                We offer <strong className="text-primary">free delivery</strong> within 15 miles of Houston, TX. 
                For locations beyond 15 miles, a small delivery fee may apply. Contact us for a custom quote!
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {cities.map((city, index) => (
                  <motion.div
                    key={city}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-2 bg-secondary/30 hover:bg-primary/10 rounded-lg px-3 py-2 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium text-foreground">{city}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl border border-primary/20">
                <p className="text-foreground font-medium mb-4">
                  Don't see your city listed? We may still deliver to your area!
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a 
                    href="tel:18329704583"
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold px-6 py-3 rounded-full shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <Phone className="w-5 h-5" />
                    Call 832 970 4583
                  </a>
                  <Link 
                    to="/browse-rentals"
                    className="inline-flex items-center justify-center gap-2 bg-secondary text-secondary-foreground font-bold px-6 py-3 rounded-full shadow hover:scale-105 transition-all duration-300"
                  >
                    Browse Rentals
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Quick Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-card rounded-2xl shadow-xl p-8 border border-border/50 lg:col-span-2"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Have a Question?</h2>
              </div>
              <p className="text-muted-foreground mb-6 text-sm">
                Send us a quick message and we'll get back to you as soon as possible.
              </p>
              <QuickContactForm />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}